import { useEffect, useLayoutEffect, useRef } from "react";
import { sameThreadId } from "../../providers/thread-detail-state";
import { shouldShowBrowserNotification } from "../layouts/notification-utils";

const MOTION_QUERY = "(prefers-reduced-motion: reduce)";

export default function useIncomingMessageHighlight({
  rootRef, notification, user, personalInboxId, selectedThreadId,
  loadedThreadId, messages = [], conversationType,
}) {
  const lastNotification = useRef(notification);
  const pending = useRef(new Map());
  const seen = useRef(new Set(
    notification?.message_id == null ? [] : [String(notification.message_id)],
  ));
  const animations = useRef(new Map());
  const observed = useRef({ threadId: null, tail: null });
  const messageKey = (message) => message?.message_id != null
    ? `id:${message.message_id}`
    : message?.client_message_id ? `client:${message.client_message_id}` : null;
  const scope = `${user?.account_id}:${user?.user?.user_id}:${personalInboxId}`;
  const previous = useRef({ scope, selectedThreadId });

  useEffect(() => {
    const running = animations.current;
    const cancel = () => {
      running.forEach((animation) => animation.cancel());
      running.clear();
    };
    const preference = window.matchMedia?.(MOTION_QUERY);
    const onChange = () => { if (preference.matches) cancel(); };
    preference?.addEventListener?.("change", onChange);
    return () => {
      preference?.removeEventListener?.("change", onChange);
      cancel();
    };
  }, []);

  // Inspect only pending arrivals after React has rendered. This also covers
  // a full message arriving from the API later than its notification preview.
  useLayoutEffect(() => {
    if (previous.current.scope !== scope) {
      pending.current.clear();
      seen.current.clear();
      if (notification?.message_id != null) seen.current.add(String(notification.message_id));
      animations.current.forEach((animation) => animation.cancel());
      animations.current.clear();
      lastNotification.current = notification;
      observed.current = { threadId: null, tail: null };
    }
    if (!sameThreadId(previous.current.selectedThreadId, selectedThreadId)) {
      pending.current.forEach((arrival) => { arrival.body = false; });
      observed.current = { threadId: null, tail: null };
    }
    previous.current = { scope, selectedThreadId };

    const queue = (arrival) => {
      const key = arrival?.message_id == null ? null : String(arrival.message_id);
      if (!key || seen.current.has(key)) return;
      seen.current.add(key);
      // Bound memory for busy inboxes without using one timeout per message.
      if (seen.current.size > 1000) seen.current.delete(seen.current.values().next().value);
      if (
        arrival.thread_id != null &&
        shouldShowBrowserNotification(arrival, user) &&
        !((arrival.message_type || arrival.sender_type) === "internal" &&
          sameThreadId(arrival.sender_id, user?.user?.user_id)) &&
        (personalInboxId == null || arrival.personal_inbox_id == null ||
          sameThreadId(personalInboxId, arrival.personal_inbox_id))
      ) {
        pending.current.set(key, {
          threadId: arrival.thread_id,
          preview: true,
          body: sameThreadId(selectedThreadId, arrival.thread_id),
          expires: Date.now() + 30000,
        });
      }
    };
    if (lastNotification.current !== notification) {
      lastNotification.current = notification;
      queue(notification);
    }

    if (sameThreadId(loadedThreadId, selectedThreadId)) {
      if (sameThreadId(observed.current.threadId, loadedThreadId)) {
        // A recovery fetch can discover a new message without a websocket
        // event. Only additions after the previous tail count as arrivals;
        // initial loads, edits, and prepending history do not.
        const anchor = observed.current.tail === null ? -1
          : messages.findIndex((message) => messageKey(message) === observed.current.tail);
        if (observed.current.tail === null || anchor >= 0) {
          messages.slice(anchor + 1).forEach((message) => queue({
            ...message, thread_id: loadedThreadId, conversation_type: conversationType,
          }));
        }
      }
      observed.current = {
        threadId: loadedThreadId, tail: messageKey(messages[messages.length - 1]),
      };
    }

    const root = rootRef.current;
    if (!root || pending.current.size === 0) return;
    if (document.visibilityState === "hidden") {
      pending.current.clear();
      return;
    }
    const previews = Array.from(root.querySelectorAll("[data-preview-message-id]"));
    const bodies = Array.from(root.querySelectorAll(".hms-message-wrapper[data-message-id]"));
    const highlight = (element) => {
      if (!element.animate || window.matchMedia?.(MOTION_QUERY).matches) return;
      // A burst shares the preview's existing fade rather than flashing it
      // repeatedly or restarting the animation on every incoming message.
      if (animations.current.has(element)) return;
      // Give the eye a moment to register the color before fading it away.
      const animation = element.animate(
        [
          { backgroundColor: "#cfe5ff", offset: 0 },
          { backgroundColor: "#cfe5ff", offset: 0.3, easing: "ease-out" },
          { backgroundColor: window.getComputedStyle(element).backgroundColor, offset: 1 },
        ],
        { duration: 1500, easing: "linear" },
      );
      animations.current.set(element, animation);
      const done = () => {
        if (animations.current.get(element) === animation) animations.current.delete(element);
      };
      animation.onfinish = done;
      animation.oncancel = done;
    };

    pending.current.forEach((arrival, id) => {
      if (arrival.expires < Date.now()) {
        pending.current.delete(id);
        return;
      }
      if (arrival.preview) {
        const preview = previews.find((element) =>
          sameThreadId(element.dataset.threadId, arrival.threadId) &&
          element.dataset.previewMessageId === id,
        );
        if (preview) {
          highlight(preview);
          arrival.preview = false;
        }
      }
      if (arrival.body) {
        const body = bodies.find((element) =>
          sameThreadId(element.dataset.threadId, arrival.threadId) &&
          element.dataset.messageId === id,
        );
        if (body) {
          highlight(body.querySelector(".hms-message-body") || body);
          arrival.body = false;
        }
      }
      if (!arrival.preview && !arrival.body) pending.current.delete(id);
    });
  });
}
